import {
  select,
  call,
  put,
  fork,
  all,
  getContext,
  takeLeading
} from "redux-saga/effects";
import * as actionTypes from "constants/actionTypes";
import { addTimingMeasureInstructionsToAction } from "utils/timing";
import { syncFirebasePath } from "sagas/firebase/internal-lib";
import { selectAccountId } from "reducers/account/meta";
import { selectAccountUser, selectAccountUsers } from "reducers/account/users";
import * as firebase from "sagas/firebase/lib";
import { throttleBy } from "utils/sagas";
import {
  TRANSFER_ACCEPTED,
  USER_INVITED,
  USER_TRANSFERRED
} from "constants/account/users";
import UserInAccountError from "errors/UserInAccountError";
import UserExistsError from "errors/UserExistsError";
import { promisaga } from "utils/as-promised";
import { reportError } from "helpers/error-reporting";
import { scrapeAcceptInviteToAccountId } from "utils/url";
import { selectUser, selectUserId } from "reducers/user";

export function* replaceAccountUserMeta(userId, data) {
  yield put(
    addTimingMeasureInstructionsToAction(
      {
        type: actionTypes.REPLACE_ACCOUNT_USER_META,
        payload: {
          id: userId,
          meta: data || {}
        }
      },
      actionTypes.SYNC_ACCOUNT_WITH_FIREBASE
    )
  );
}

export function* replaceAccountUser({ key, data }) {
  if (data) {
    yield put({
      type: actionTypes.REPLACE_ACCOUNT_USER,
      payload: {
        id: key,
        ...data
      }
    });
    yield fork(
      syncFirebasePath,
      `users/${key}/meta`,
      data => replaceAccountUserMeta(key, data),
      actionTypes.CANCEL_ACCOUNT_SYNC
    );
  }
}

export function* replaceAccountUsers({ data }) {
  yield put({
    type: actionTypes.REPLACE_ACCOUNT_USERS,
    payload: data || {}
  });

  if (data) {
    yield all(
      Object.keys(data).map(userId =>
        fork(
          syncFirebasePath,
          `users/${userId}/meta`,
          data => replaceAccountUserMeta(userId, data),
          actionTypes.CANCEL_ACCOUNT_SYNC
        )
      )
    );
  }
}

export function* flushAccountUserToFirebase(action) {
  const { userId } = action.payload;
  const accountId = yield select(selectAccountId);
  const user = yield select(selectAccountUser, userId);

  if (user) {
    const accountUser = {
      ...user,
      meta: null,
      synced: null
    };
    yield call(
      firebase.set,
      `accounts/${accountId}/users/${userId}`,
      accountUser
    );
  } else {
    yield call(firebase.remove, [`accounts/${accountId}/users/${userId}`]);
  }
}

export function* inviteUser({ payload: { email, name, role } }) {
  const accountUsers = yield select(selectAccountUsers);
  if (
    Object.values(accountUsers || {})
      .map(({ meta }) => meta)
      .find(({ email: userEmail } = {}) => userEmail === email)
  ) {
    throw new UserInAccountError();
  }

  const accounts = yield getContext("accounts");
  const isExistingUser = yield call(accounts.exists, { email });

  if (isExistingUser) {
    throw new UserExistsError();
  }

  try {
    const { email: inviter } = yield select(selectUser);
    yield call(accounts.inviteUser, { email, name, role, inviter });
  } catch (e) {
    yield call(reportError, e);
    throw e;
  }
}

export function* transferUser({ payload: { email, name, role } }) {
  const { id: inviterUid } = yield select(selectUser);
  const { getAccounts, transferUser, addUser } = yield getContext("accounts");
  const userHasAccount = yield call(getAccounts, { email });
  if (userHasAccount) {
    yield call(transferUser, {
      email,
      name,
      role,
      inviterUid
    });
  } else {
    yield call(addUser, {
      email,
      role,
      inviterUid
    });
  }
}

export function* acceptTransfer({ payload: { accountId } }) {
  try {
    const userId = yield select(selectUserId);
    const accounts = yield getContext("accounts");
    yield call(accounts.acceptTransfer, { accountId, userId });
    yield call(scrapeAcceptInviteToAccountId);
    const auth = yield getContext("auth");
    yield call(auth.logout);
  } catch (e) {
    yield call(reportError, e);
    throw e;
  }
}

export default function* users() {
  yield fork(
    throttleBy,
    "userId",
    500,
    actionTypes.FLUSH_ACCOUNT_USER_TO_FIREBASE,
    flushAccountUserToFirebase
  );
  yield takeLeading(USER_INVITED, promisaga(inviteUser));
  yield takeLeading(USER_TRANSFERRED, promisaga(transferUser));
  yield takeLeading(TRANSFER_ACCEPTED, promisaga(acceptTransfer));
}
